/** @format */

import {
  ATCEventChildSku,
  ATCEventSkuData,
  BundleData,
  ISkuLocation,
  MyCustomizerMap,
  PersonalizationState,
  ProductListSEO,
  ProductSku,
  ProductStyle,
  ProductsData,
  PromotionDetailsMapV1,
  PromotionDetailsMapV2,
  SddInventory,
  Shipping,
  SkuEDDResponse,
} from '@shared/data-access/interfaces';
import { CategoryTwo, CollectionProduct } from '@pdp/data-access/interfaces';
import { createAction, props } from '@ngrx/store';

import { DynamicProductTemplate } from '@pdp/data-access/enums';
import type { PdpProductProfile } from '@pdp/data-access/constants';

export const getProductDataAction = createAction(
  '[Product Data] Received',
  props<{
    productSkus: ProductSku[];
    productStyle: ProductStyle | Record<string, never>;
  }>(),
);

export const productStyleAndSkusInitialized = createAction(
  '[Product State Initialization] Product Style & SKUs Initialized',
  props<{
    productSkus: ProductSku[];
    productStyle: ProductStyle | Record<string, never>;
  }>(),
);

export const bundleProductDataInitialized = createAction(
  '[Product State Initialization] Bundle Product Data Initialized',
  props<{
    bundleData: BundleData | Record<string, never>;
    bundleProductsData: ProductsData[];
  }>(),
);

export const collectionsSet = createAction(
  '[Collection Summary] Collections Set',
  props<{ collections: CollectionProduct[] }>(),
);

export const skuResolutionChangedViaAttributes = createAction(
  '[Attributes] SKU Resolution Changed',
  props<{ resolvedSku: ProductSku | null | undefined }>(),
);

export const skuResolutionChangedViaInventory = createAction(
  '[Inventory] SKU Resolution Changed',
  props<{ resolvedSku: ProductSku | undefined }>(),
);

export const singleSkuProductResolved = createAction(
  '[Product State Initialization] Single SKU Product Resolved',
  props<{ resolvedSku: ProductSku }>(),
);

export const skuResolvedViaCustomization = createAction(
  '[Customization] SKU Resolved',
  props<{ resolvedSku: ProductSku }>(),
);

export const skuResolutionActions = [
  skuResolutionChangedViaAttributes,
  skuResolutionChangedViaInventory,
  singleSkuProductResolved,
  skuResolvedViaCustomization,
] as const;

export const fetchSDDInfo = createAction(
  '[Product Selection] Fetch SDD Inventory',
  props<{ zipCode: string; skuId: number }>(),
);

export const fetchSDDInfoSuccess = createAction(
  '[Header Location Effects] SDD Info Fetched',
  props<{ sdd: SddInventory }>(),
);

export const fetchSDDInfoError = createAction(
  '[Header Location Effects] SDD Info Fetch Errored',
  props<{ error: string }>(),
);

export const eddInfoFetched = createAction(
  '[Header Location Effects] EDD Info Fetched',
  props<{ eddInfo: SkuEDDResponse }>(),
);

export const eddInfoFetchErrored = createAction(
  '[Header Location Effects] EDD Info Fetch Errored',
  props<{ error: string }>(),
);

export const inventoryFetched = createAction(
  '[Product Selection Inventory] Inventory Fetched',
  props<{ ecommInventory: ISkuLocation[]; bopisInventory: ISkuLocation[] }>(),
);

export const inventoryFetchErrored = createAction(
  '[Product Selection Inventory] Inventory Fetch Errored',
);

export const personalizationOptionsUpdated = createAction(
  '[Personalization] Personalization Options Updated',
  props<{ selectedPersonalizationOptions: PersonalizationState }>(),
);

export const primaryCategoryFetched = createAction(
  '[Product State Initialization] Primary Category Fetched',
  props<{ primaryCategory: CategoryTwo | ProductListSEO }>(),
);

export const productHasBothEmptyAndNonEmptyPriceChecked = createAction(
  '[Product State Initialization] Checked If Product Has Both Empty and Non-Empty Price',
  props<{ hasBothEmptyAndNonEmptyPrice: boolean }>(),
);

export const baseShippingInfoProcessed = createAction(
  '[Product Selection Effects] Base Shipping Info Processed',
  props<{ shippingInfo: Shipping | Record<string, never> }>(),
);

export const resolvedSkuShippingInfoProcessed = createAction(
  '[Product Selection Effects] Resolved SKU Shipping Info Processed',
  props<{ shippingInfo: Shipping | Record<string, never> }>(),
);

export const promotionsFetched = createAction(
  '[Post-Template Load] Promotions Fetched',
  props<{ promotionData: PromotionDetailsMapV1 | PromotionDetailsMapV2 }>(),
);

export const customProductUpdated = createAction(
  '[Custom Template] Customizations Updated',
  props<{ customProductData: MyCustomizerMap }>(),
);

export const setFBTProducts = createAction(
  '[Product Selection] Set Freqently Bought Together Products',
  props<{ FBTProducts: [ATCEventSkuData, ...ATCEventChildSku[]] | [] }>(),
);

export const styliticsShopTheLookWidgetMounted = createAction(
  '[Stylitics] Shop The Look Widget Mounted',
  props<{ styliticsWidgetShown: boolean }>(),
);

export const dynamicTemplateChosen = createAction(
  '[Product Container] Dynamic Template Chosen',
  props<{ templateName: DynamicProductTemplate }>(),
);

export const isSolePanel = createAction(
  '[PDP App] Is Sole Panel',
  props<{ isSolePanel: boolean }>(),
);

export const dynamicTemplateNameSSRLoaded = createAction(
  '[Product State Initialization] Dynamic Template Name Loaded From SSR',
  props<{ templateName: DynamicProductTemplate }>(),
);

export const setTemplateProfile = createAction(
  '[Product Container] Template Profile Set',
  props<{ templateProfile: PdpProductProfile | null }>(),
);

export const outOfStockTemplateInitialized = createAction(
  '[Out of Stock Template] Template Initialized',
);
